import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  notification,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from "antd";
import IMAGES from "../../const/Image";
import { components, getRowClassName } from "../../utils/utils";
import SettingRequest from "./SettingRequest";

const initialFilter = {
  page: 1,
  search: "",
};

const defaultPageSize = 10;

function Setting() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [settingsList, setSettingsList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filter, setFilter] = useState(initialFilter);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);

  const isEditMode = Boolean(selectedSetting?.id);

  const normalizeListResponse = (response) => {
    const payload = response?.data?.data;

    if (Array.isArray(payload)) {
      return {
        rows: payload,
        total: payload.length,
      };
    }

    const rows =
      payload?.data ||
      payload?.rows ||
      payload?.list ||
      payload?.settings ||
      [];

    return {
      rows,
      total:
        payload?.totalItems ||
        payload?.total ||
        response?.data?.total ||
        rows.length,
    };
  };

  const fetchSettings = useCallback(() => {
    setLoading(true);
    SettingRequest.getSettings({
      page: filter.page,
      search: filter.search,
      limit: defaultPageSize,
    })
      .then((response) => {
        const { rows, total } = normalizeListResponse(response);
        setSettingsList(rows);
        setTotalRecords(total);
      })
      .catch(() => {
        setSettingsList([]);
        setTotalRecords(0);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter.page, filter.search]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const tableData = useMemo(
    () =>
      settingsList.map((item, index) => ({
        ...item,
        key: item.id || index,
      })),
    [settingsList]
  );

  const openCreateModal = () => {
    setSelectedSetting(null);
    form.resetFields();
    form.setFieldsValue({
      // status: true,
      otherField: "value",
    });
    setModalOpen(true);
  };

  const normalizeDetailResponse = (response) => response?.data?.data || {};

  const openEditModal = async (record) => {
    setLoading(true);
    try {
      const response = await SettingRequest.getSettingDetail(record.id);
      const detail = normalizeDetailResponse(response);

      setSelectedSetting(detail);
      form.setFieldsValue({
        price_multiplier: detail?.price?.price_multiplier,
        // status: detail?.status === undefined ? true : detail.status === 1,
      });
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedSetting(null);
    form.resetFields();
  };

  const refreshAfterMutation = () => {
    if (filter.page === 1) {
      fetchSettings();
      return;
    }

    setFilter((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const onFinish = async (values) => {
    setSubmitLoading(true);
    const payload = {
      ...(isEditMode ? { id: selectedSetting.id } : {}),
      price: {
        price_multiplier: Number(values.price_multiplier),
      },
      // status: values.status ? 1 : 0,
    };

    if (!isEditMode) {
      payload.otherField = "value";
    }

    try {
      const response = isEditMode
        ? await SettingRequest.updateSetting(payload)
        : await SettingRequest.createSetting(payload);

      notification.success({
        message:
          response?.data?.message ||
          `Setting ${isEditMode ? "updated" : "created"} successfully`,
      });
      closeModal();
      refreshAfterMutation();
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = (id) => {
    setLoading(true);
    SettingRequest.deleteSetting(id)
      .then((response) => {
        notification.success({
          message: response?.data?.message || "Setting deleted successfully",
        });
        fetchSettings();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = (pagination) => {
    setFilter((prev) => ({
      ...prev,
      page: pagination.current,
    }));
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "sno",
      key: "sno",
      width: 90,
      render: (_, __, index) => (filter.page - 1) * defaultPageSize + index + 1,
    },
    {
      title: "Setting",
      dataIndex: "otherField",
      key: "otherField",
      render: (value) => value || "Price Multiplier",
    },
    {
      title: "Price Multiplier",
      dataIndex: ["price", "price_multiplier"],
      key: "price_multiplier",
      render: (value) => value ?? "-",
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   align: "center",
    //   width: 160,
    //   render: (status) => (
    //     <Tag color={status === 1 ? "green" : "red"}>
    //       {status === 1 ? "Active" : "Inactive"}
    //     </Tag>
    //   ),
    // },
    // {
    //   title: "Created At",
    //   dataIndex: "createdAt",
    //   key: "createdAt",
    //   render: (value) => (value ? moment(value).format("DD/MM/YYYY") : "-"),
    // },
    // {
    //   title: "Updated At",
    //   dataIndex: "updatedAt",
    //   key: "updatedAt",
    //   render: (value) => (value ? moment(value).format("DD/MM/YYYY") : "-"),
    // },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      fixed: "right",
      width: 140,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Setting">
            <span
              className="action-icon-bg"
              onClick={() => openEditModal(record)}
            >
              <img src={IMAGES.EditIcon} alt="Edit" />
            </span>
          </Tooltip>
          <Popconfirm
            title="Delete this setting?"
            description="Are you sure you want to delete this setting?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record.id)}
          >
            <span className="action-icon-bg">
              <img src={IMAGES.DeleteIcon} alt="Delete" />
            </span>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="">
        <Row gutter={[8, 8]} justify="space-between" align="middle">
          <Col>
            <h1 className="app-heading-med">Settings</h1>
          </Col>
          <Col>
            <div style={{ display: "flex", gap: "1rem" }}>
              {/* <Input
                placeholder="Search settings"
                size="large"
                className="FilterSearchBox"
                value={filterForm.search}
                onChange={(event) =>
                  setFilterForm((prev) => ({
                    ...prev,
                    search: event.target.value,
                  }))
                }
                onKeyPress={(event) => {
                  if (event.key === "Enter") {
                    handleSearch();
                  }
                }}
              /> */}
              {/* <Button
                onClick={handleSearch}
                type="primary"
                className="addcity-btn"
                style={{ height: "39px" }}
              >
                <SearchOutlined />
              </Button>
              <Button
                type="primary"
                className="addcity-btn"
                onClick={resetFilters}
                style={{ height: "39px" }}
              >
                Reset
              </Button> */}
              <Button
                type="primary"
                className="addcity-btn"
                onClick={openCreateModal}
                style={{ height: "39px" }}
              >
                <img src={IMAGES.AddIconFilters} alt="Add" />
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <div style={{ marginTop: "20px" }}>
        <Table
          size="middle"
          bordered
          rowClassName={getRowClassName}
          components={components}
          columns={columns}
          loading={loading}
          dataSource={tableData}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          pagination={{
            total: totalRecords,
            current: filter.page,
            pageSize: defaultPageSize,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </div>

      <Modal
        title={isEditMode ? "Update Setting" : "Create Setting"}
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item
            label="Price Multiplier"
            name="price_multiplier"
            rules={[
              {
                required: true,
                message: "Please enter price multiplier",
              },
            ]}
          >
            <InputNumber
              size="large"
              min={0}
              placeholder="Enter price multiplier"
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* <Form.Item label="Status" name="status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item> */}

          <Row justify="end">
            <Space>
              <Button onClick={closeModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {isEditMode ? "Update" : "Create"}
              </Button>
            </Space>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default Setting;
